import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Button, ButtonToggles, Input, TimeMaskedInput } from 'components';
import { HTTPStatusCode } from 'config/const';
import { Appearance } from 'design/types';
import validations from 'lib/Validations';
import Form from 'screens/Error/Form';
import { selectAPIRequestErrors, selectIsLoadingAPIRequest } from 'store/ApiClient';
import { createDaySegmentRequest, DaySegments, updateDaySegmentRequest } from 'store/Collections/DaySegments';
import { IStore } from 'store/types';
import { dayOptions, IApiErrorPayloadWithCode, ICreateUpdateDaySegmentRequest, IDaySegment } from 'types';
import { stripSpaces } from 'utils/strings';
import { ActionSection } from '../../../styles';
import { DaySegmentCard, DaySegmentFieldRow, StyledFieldWrapper, StyledTag, TimeFieldsWrapper } from '../../shared/styles';

interface IDaySegmentFormProps {
  siteId: string;
  daySegment?: IDaySegment;
  onClose?(): void;
}

const getErrorTag = (daySegmentError?: IApiErrorPayloadWithCode) => {
  if (!daySegmentError) return null;

  let error;

  switch (daySegmentError.statusCode) {
    case HTTPStatusCode.CONFLICT:
      if (daySegmentError.errorMessages?.[0].startsWith('grace_period_policy')) {
        error = { label: 'Segments need to be at least twice as long as the grace period.', dataCy: 'grace-period-error' };
      } else if (daySegmentError.errorMessages?.[0].startsWith('business_day_start')) {
        error = { label: 'This day segment crosses the start of business day.\nAdjust the segment times or the start of business day before saving.', dataCy: 'business-day-start-error' };
      } else {
        error = { label: 'Day segments names must be unique', dataCy: 'name-error' };
      }
      break;
    case HTTPStatusCode.UNPROCESSABLE_ENTITY:
      error = { label: 'Day segments cannot overlap', dataCy: 'overlap-error' };
      break;
    default:
      error = { label: 'An error occurred. Please try again.', dataCy: 'unknown-error' };
      break;
  }
  return <StyledTag appearance={Appearance.DANGER} label={error.label} dataCy={error.dataCy} />;
};

export const DaySegmentForm: React.FC<IDaySegmentFormProps> = ({ siteId, daySegment, onClose }) => {
  const dispatch = useDispatch();
  const actionId = daySegment ? DaySegments.UPDATE_DAY_SEGMENT_REQUEST : DaySegments.CREATE_DAY_SEGMENT_REQUEST;
  const isLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, actionId));
  const daySegmentError = useSelector((state: IStore) => selectAPIRequestErrors(state, actionId)) as IApiErrorPayloadWithCode;

  const validationSchema = Yup.object().shape({
    name: Yup.string().trim().required('You must enter a name for the day segment').max(50, 'Name must be at most 50 characters'),
    days: Yup.array().of(Yup.string()).min(1, 'At least one day must be selected'),
    start: validations.timeValidationSchema,
    end: validations.timeValidationSchema.test('time-match', 'Cannot match start', (value, ctx) => ctx.parent.start !== value),
  });

  const { dirty, errors, touched, getFieldProps, handleSubmit, setFieldValue, values } = useFormik({
    initialValues: daySegment || {
      name: '',
      days: [],
      start: '',
      end: '',
    },
    onSubmit: (vals: ICreateUpdateDaySegmentRequest) => {
      const values = { ...vals, name: stripSpaces(vals.name) };
      if (daySegment) {
        dispatch(updateDaySegmentRequest(siteId, daySegment.id, values, onClose));
      } else {
        dispatch(createDaySegmentRequest(siteId, values, onClose));
      }
    },
    validateOnBlur: true,
    validationSchema,
    enableReinitialize: true,
  });

  const endsNextDay = useMemo(() => {
    let endsNextDay = false;

    if (values.start && values.end && !errors.start && !errors.end) {
      const date = dayjs().format('YYYY-MM-DD');
      const startDate = dayjs(`${date} ${values.start}`);
      const endDate = dayjs(`${date} ${values.end}`);

      endsNextDay = endDate <= startDate;
    }

    return endsNextDay;
  }, [values, errors]);

  const validateField = (field: 'name' | 'days' | 'start' | 'end') => Boolean(touched[field] && errors[field]);

  return (
    <DaySegmentCard data-cy="day-segment-form">
      {getErrorTag(daySegmentError)}

      <Form formId={actionId} onSubmit={handleSubmit}>
        <StyledFieldWrapper label="Day Segment Name" data-cy="name" message={validateField('name') ? errors.name : ''} hasError={validateField('name')}>
          <Input {...getFieldProps('name')} maxLength={50} />
        </StyledFieldWrapper>

        <DaySegmentFieldRow>
          <StyledFieldWrapper label="Days" data-cy="days" message={validateField('days') ? (errors.days as string) : ''} hasError={validateField('days')}>
            <ButtonToggles values={getFieldProps('days').value} options={dayOptions} onChange={(val) => setFieldValue('days', val)} className="notranslate" />
          </StyledFieldWrapper>
          <TimeFieldsWrapper>
            <StyledFieldWrapper label="Start Time" data-cy="start" message={validateField('start') ? errors.start : ''} hasError={validateField('start')}>
              <TimeMaskedInput {...getFieldProps('start')} onChange={(val) => setFieldValue('start', val)} />
            </StyledFieldWrapper>
            <StyledFieldWrapper
              label="End Time"
              data-cy="end"
              message={validateField('end') ? errors.end : ''}
              hasError={validateField('end')}
              icon={endsNextDay ? 'CalendarPlus' : undefined}
              tooltipIcon="This shift ends on the next calendar day"
              id="endTime"
            >
              <TimeMaskedInput {...getFieldProps('end')} onChange={(val) => setFieldValue('end', val)} />
            </StyledFieldWrapper>
          </TimeFieldsWrapper>
        </DaySegmentFieldRow>

        <ActionSection>
          <Button appearance="white" label="Cancel" onClick={onClose} disabled={isLoading} />
          <Button type="submit" label="Save Day Segment" isProcessing={isLoading} disabled={isLoading || !dirty} />
        </ActionSection>
      </Form>
    </DaySegmentCard>
  );
};
